import { generatePath as reactRouterGeneratePath } from 'react-router';

type PathParams = Record<string, string | null | undefined>;

/**
 * Encodes a single path segment. Mirrors the historical `encodeURI` output
 * (so commas and other sub-delimiters stay un-escaped, matching the URLs the
 * app has always emitted), but additionally escapes the structural delimiters
 * `encodeURI` leaves intact (`/`, `?`, `#`). Without this a slug like
 * `category/slug` would be emitted verbatim and parsed as two segments.
 */
function encodeSegment(value: string): string {
  return encodeURI(value).replace(/\//g, '%2F').replace(/\?/g, '%3F').replace(/#/g, '%23');
}

/**
 * React Router v6's `generatePath` changed two behaviours the route builders
 * rely on: it no longer URL-encodes interpolated params, and it no longer throws
 * when a required (non-optional) param is missing/empty. This wrapper restores
 * the v5 semantics so building keeps producing encoded paths and still surfaces
 * missing params instead of silently dropping a segment.
 */
export function generatePath(pattern: string, params: PathParams = {}): string {
  const encodedParams: Record<string, string | null> = {};

  Object.entries(params).forEach(([key, value]) => {
    const isOptional = pattern.includes(`:${key}?`);

    if (!isOptional && (value === undefined || value === null || value === '')) {
      throw new Error(`Expected "${key}" to match "[^\\/]+?", but received "${value ?? ''}"`);
    }

    encodedParams[key] = value === undefined || value === null ? null : encodeSegment(value);
  });

  return reactRouterGeneratePath(pattern, encodedParams);
}
